import { motion } from "framer-motion";
import { TravelIcon, AbroadIcon, MoveInIcon, BooksIcon } from "../lib/icons";
import { FadeUp } from "./Reveal";
import { TILE_BOUNCE, useTapBounce } from "../lib/tapBounce";
import { NoteEyebrow } from "./NoteEyebrow";
import { EmSwash } from "./EmSwash";
import { FUNDS, IMAGES } from "../data/content";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// Same card idiom as the FAQ category cards: bone card, typed note,
// solid palette tile, serif title, plain blurb. Hover washes in the tint.
const FUND_META = [
  { bg: "bg-tangerine", border: "hover:border-tangerine", tint: "rgba(200, 106, 46, 0.09)", icon: TravelIcon },
  { bg: "bg-cobalt",    border: "hover:border-cobalt",    tint: "rgba(3, 34, 244, 0.07)",   icon: AbroadIcon },
  { bg: "bg-grass",     border: "hover:border-grass",     tint: "rgba(10, 155, 6, 0.09)",   icon: MoveInIcon },
  { bg: "bg-blossom",   border: "hover:border-blossom",   tint: "rgba(253, 152, 217, 0.18)", icon: BooksIcon },
];

// Typed caption (audit L2: the section's one handwritten note is the eyebrow).
// Card colour lives in the dash; the text stays ink so 13px is legible on bone
// (blossom/tangerine/grass text on white all fall under 4:1 contrast).
const FundNote = ({ note, bg }) => (
  <p className="relative mt-5 flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.18em] text-ink" data-testid="fund-card-note">
    <span aria-hidden="true" className={`h-1.5 w-5 shrink-0 rounded-full ${bg}`} />
    {note}
  </p>
);

const FundCard = ({ f, meta }) => {
  const Icon = meta.icon;
  const [state, onTap] = useTapBounce();
  return (
    <motion.div
      initial="rest"
      whileHover="hover"
      animate={state}
      onTap={onTap}
      className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border-2 border-ink/10 bg-bone p-6 transition-colors lg:p-7 ${meta.border}`}
      data-testid={`fund-card-${slug(f.title)}`}
    >
      <motion.span
        aria-hidden="true"
        className="absolute inset-0"
        style={{ backgroundColor: meta.tint }}
        variants={{ rest: { opacity: 0, y: "100%" }, hover: { opacity: 1, y: "0%" } }}
        transition={{ type: "spring", stiffness: 260, damping: 30 }}
      />
      <motion.span
        className={`tile-clay relative inline-flex h-11 w-11 items-center justify-center rounded-xl ${meta.bg} text-sand`}
        variants={{ rest: { y: 0, rotate: 0, scale: 1 }, hover: { y: -5, rotate: -4, scale: 1.05 }, bounce: { ...TILE_BOUNCE, rotate: [0, -6, 0, -2, 0] } }}
        transition={{ type: "spring", stiffness: 420, damping: 14, mass: 0.6 }}
        data-testid="fund-card-tile"
      >
        <Icon className="h-5 w-5" aria-hidden="true" />
      </motion.span>
      <FundNote note={f.note} bg={meta.bg} />
      <h3 className="relative mt-4 font-serif text-3xl leading-none text-ink">{f.title}</h3>
      <p className="relative mt-4 text-base leading-relaxed text-olive">{f.body}</p>
    </motion.div>
  );
};

export const Impact = () => (
  <section className="bg-beige">
    <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8 lg:py-32">
      <FadeUp>
        <NoteEyebrow accent="bg-blossom">what your gift covers</NoteEyebrow>
        <h2 className="mt-5 max-w-3xl font-serif text-3xl lg:text-[3.5rem] leading-[1.02] tracking-tight text-ink">
          The costs aid forgets. The moments that define a <EmSwash color="text-blossom">college career.</EmSwash>
        </h2>
      </FadeUp>

      <div className="mt-16 grid grid-cols-1 items-stretch gap-6 lg:grid-cols-12">
        {/* Ramen photo + quote — left */}
        <FadeUp className="lg:col-span-6">
          <div className="relative h-full min-h-[420px] overflow-hidden rounded-md border border-border">
            <img
              src={IMAGES.ramen}
              alt="A student reading a novel over a cup of ramen — a quiet moment of college life"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
            <p className="absolute bottom-8 left-8 max-w-xs font-serif text-3xl italic text-sand">
              “A little help, at the right time, goes a long way.”
            </p>
          </div>
        </FadeUp>

        {/* Fund cards — 2×2 grid next to the photo on desktop, single column on mobile */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:col-span-6">
          {FUNDS.map((f, i) => (
            <FadeUp key={f.title} delay={i * 0.06} className="h-full">
              <FundCard f={f} meta={FUND_META[i % FUND_META.length]} />
            </FadeUp>
          ))}
        </div>
      </div>
    </div>
  </section>
);
